import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Plus } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import { stripHtml } from 'utils/util/util';
import AutoSaveInfoBox from 'views/Widgets/Forms/AutoSaveInfoBox';
import { useAutosave } from 'views/Widgets/Forms/hooks';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import type { CalibrationRecommendation } from '../types';
import { RecommendationInput } from './RecommendationInput';

export type CommentDraft = {
  content: string;
  recommendation?: CalibrationRecommendation;
  isPrivate: boolean;
};

// The draft is stored remotely with the same autosave as the performance
// cycle forms, which needs the field types to check a restored draft against
const DRAFT_INPUTS = [
  { name: 'content', type: INPUT_TYPES.RICH_TEXT_EDITOR },
  { name: 'recommendation', type: INPUT_TYPES.CUSTOM_COMPONENT },
  { name: 'isPrivate', type: INPUT_TYPES.SWITCH },
];
const NO_SUBMITTED_DRAFT = {};

export type CommentComposerProps = {
  isOpen: boolean;
  // Replies have no recommendation and are always open
  isReply?: boolean;
  composerContent: string;
  onComposerContentChange: (value: string) => void;
  composerRecommendation?: CalibrationRecommendation;
  onComposerRecommendationChange?: (value?: CalibrationRecommendation) => void;
  composerIsPrivate: boolean;
  onComposerIsPrivateChange: (value: boolean) => void;
  composerError: string | null;
  isSubmitting: boolean;
  onOpen?: () => void;
  onCancel: () => void;
  // Called with a function to run once the comment is posted, which deletes
  // the draft and then closes the composer
  onSubmit: (onPosted: () => void) => void;
  canAddComment?: boolean;
  draftKey: string;
  onDraftRestored: (draft: CommentDraft) => void;
  onPendingDraftSaveChange?: (hasPendingDraftSave: boolean) => void;
};

export const CommentComposer: FC<CommentComposerProps> = ({
  isOpen,
  isReply = false,
  composerContent,
  onComposerContentChange,
  composerRecommendation,
  onComposerRecommendationChange,
  composerIsPrivate,
  onComposerIsPrivateChange,
  composerError,
  isSubmitting,
  onOpen,
  onCancel,
  onSubmit,
  canAddComment = true,
  draftKey,
  onDraftRestored,
  onPendingDraftSaveChange,
}) => {
  const { formatMessage } = useIntl();

  const {
    state: draftState,
    latestObjectFetched: savedDraft,
    stash: saveDraft,
    deleteRemote: deleteDraft,
    hasPendingChanges: hasPendingDraftSave,
  } = useAutosave({
    key: draftKey,
    enabled: canAddComment,
    inputs: DRAFT_INPUTS,
    submittedObject: NO_SUBMITTED_DRAFT,
  });

  // Read through a ref so a new callback from the parent doesn't re-apply a
  // draft that was already restored, or deleted since
  const onDraftRestoredRef = useRef(onDraftRestored);
  onDraftRestoredRef.current = onDraftRestored;

  useEffect(() => {
    const draft = savedDraft as Partial<CommentDraft>;
    if (draftState !== 'SUCCESS' || !draft.content) {
      return;
    }
    onDraftRestoredRef.current({
      content: draft.content,
      recommendation: draft.recommendation,
      isPrivate: draft.isPrivate ?? false,
    });
  }, [draftState, savedDraft]);

  useEffect(() => {
    if (draftState === 'ERROR') {
      toast.error(
        formatMessage({
          id: 'app.views.widgets.dashboards.calibration_dashboard.comments.draft_loading_error',
          defaultMessage:
            'Error loading existing draft data. Please refresh the page to load the latest. If the problem persists, please contact support.',
        })
      );
    }
  }, [draftState, formatMessage]);

  useEffect(() => {
    onPendingDraftSaveChange?.(hasPendingDraftSave);
  }, [hasPendingDraftSave, onPendingDraftSaveChange]);

  // A closed reply unmounts, so stop reporting its pending save
  useEffect(
    () => () => onPendingDraftSaveChange?.(false),
    [onPendingDraftSaveChange]
  );

  // Posting, cancelling and discarding all end the draft. Close only once the
  // draft is deleted, so reopening the modal can't load it again.
  const deleteDraftAndClose = useCallback(
    () => deleteDraft(onCancel),
    [deleteDraft, onCancel]
  );

  const object = useMemo(
    () => ({
      content: composerContent,
      recommendation: composerRecommendation,
      isPrivate: composerIsPrivate,
    }),
    [composerContent, composerRecommendation, composerIsPrivate]
  );

  const inputs = useMemo(
    () => [
      {
        type: INPUT_TYPES.RICH_TEXT_EDITOR,
        name: 'content',
        label: isReply ? undefined : (
          <span className="fw-semibold">
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.comments.add_comment_label"
              defaultMessage="Add a calibration comment"
            />
          </span>
        ),
        autoFocus: true,
        placeholder: isReply
          ? formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.comments.reply_label',
              defaultMessage: 'Reply',
            })
          : formatMessage({
              id: 'app.views.widgets.dashboards.calibration_dashboard.comments.add_comment_placeholder',
              defaultMessage:
                'Share calibration context for this participant...',
            }),
        editorConfig: {
          heightMin: 140,
        },
      },
      ...(isReply
        ? []
        : [
            {
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              name: 'recommendation',
              label: (
                <FormattedMessage
                  id="app.views.widgets.dashboards.calibration_dashboard.comments.recommendation_label"
                  defaultMessage="Recommendation"
                />
              ),
              component: RecommendationInput,
            },
          ]),
      {
        type: INPUT_TYPES.SWITCH,
        name: 'isPrivate',
        label: (
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.comments.field.private"
            defaultMessage="Make this comment private (visible only to calibration admins)"
          />
        ),
      },
    ],
    [formatMessage, isReply]
  );

  const onChange = useCallback(
    (newObject: {
      content?: string;
      recommendation?: CalibrationRecommendation;
      isPrivate?: boolean;
    }) => {
      const draft: CommentDraft = {
        content: newObject.content || '',
        recommendation: newObject.recommendation,
        isPrivate: newObject.isPrivate ?? false,
      };
      // The editor also reports the value it was given, which isn't a change
      if (
        draft.content === composerContent &&
        draft.recommendation === composerRecommendation &&
        draft.isPrivate === composerIsPrivate
      ) {
        return;
      }
      if (draft.content !== composerContent) {
        onComposerContentChange(draft.content);
      }
      if (draft.recommendation !== composerRecommendation) {
        onComposerRecommendationChange?.(draft.recommendation);
      }
      if (draft.isPrivate !== composerIsPrivate) {
        onComposerIsPrivateChange(draft.isPrivate);
      }
      saveDraft(draft);
    },
    [
      composerContent,
      composerRecommendation,
      composerIsPrivate,
      onComposerContentChange,
      onComposerRecommendationChange,
      onComposerIsPrivateChange,
      saveDraft,
    ]
  );

  const hasDraft =
    draftState === 'SUCCESS' && !!stripHtml(composerContent, true).trim();

  const callback = useCallback(
    (data?: unknown, error?: unknown) => {
      if (!error && data) {
        onSubmit(deleteDraftAndClose);
      }
    },
    [onSubmit, deleteDraftAndClose]
  );

  const renderForm = useCallback(
    (children: React.ReactNode, _button: React.ReactNode) => (
      <div className="mt-3">
        {children}
        {composerError && (
          <div className="text-danger small mb-3">{composerError}</div>
        )}
        <div className="d-flex align-items-center gap-3">
          <Button
            color="primary"
            size="sm"
            type="submit"
            disabled={isSubmitting}
          >
            {isReply ? (
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.comments.submit_reply"
                defaultMessage="Post reply"
              />
            ) : (
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.comments.submit_comment"
                defaultMessage="Post comment"
              />
            )}
          </Button>
          {/* With a draft, "Discard draft" is the only way to cancel */}
          {!hasDraft && (
            <Button
              color="link"
              size="sm"
              type="button"
              className="px-0"
              onClick={deleteDraftAndClose}
              disabled={isSubmitting}
            >
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.comments.cancel_comment"
                defaultMessage="Cancel"
              />
            </Button>
          )}
          {hasDraft && (
            <AutoSaveInfoBox
              onDiscardDraft={deleteDraftAndClose}
              disabled={isSubmitting}
              discardHoverText={formatMessage({
                id: 'app.views.widgets.dashboards.calibration_dashboard.comments.discard_draft.hover',
                defaultMessage:
                  "Your comment hasn't been posted yet. Click to discard this draft.",
              })}
              discardConfirmText={formatMessage({
                id: 'app.views.widgets.dashboards.calibration_dashboard.comments.discard_draft.confirm',
                defaultMessage:
                  'Are you sure you want to discard this draft comment?',
              })}
            />
          )}
        </div>
      </div>
    ),
    [
      composerError,
      isSubmitting,
      isReply,
      hasDraft,
      deleteDraftAndClose,
      formatMessage,
    ]
  );

  if (!isOpen) {
    return (
      <Button
        color="secondary"
        outline
        size="sm"
        type="button"
        className="d-inline-flex align-items-center gap-2"
        onClick={onOpen}
        // Wait for a saved draft before starting a new comment over it
        disabled={!canAddComment || draftState === 'LOADING'}
      >
        <Plus size={16} aria-hidden="true" />
        <FormattedMessage
          id="app.views.widgets.dashboards.calibration_dashboard.comments.add_comment"
          defaultMessage="Add comment"
        />
      </Button>
    );
  }

  return (
    <ValidatedForm
      object={object}
      inputs={inputs}
      onChange={onChange}
      callback={callback}
      renderForm={renderForm}
      hideSubmitButton
      isSubmitting={isSubmitting}
      disableUnsavedChangesPrompt
    />
  );
};
